import {
  IContactRepository,
  PaginatedContactMessagesResult,
  ContactMessageDetailResult,
} from '../../domain/repositories/contact.repository.interface';
import { IHttpClient } from '../../application/interfaces/http-client.interface';
import { ContactMessage, ContactMessageStatusType } from '../../domain/entities/contact-message.entity';
import { ContactRecipient } from '../../domain/entities/contact-recipient.entity';
import { ContactSettings } from '../../domain/entities/contact-settings.entity';
import { ContactEmailDelivery } from '../../domain/entities/contact-email-delivery.entity';
import { CreateContactMessageDTO } from '../../application/dtos/contact/create-contact-message.dto';
import {
  ContactMessageFiltersDTO,
  ContactMessageResponseDTO,
  ContactMessageDetailResponseDTO,
  ContactMessageStatsDTO,
  ContactEmailDeliveryResponseDTO,
} from '../../application/dtos/contact/contact-message.dto';
import {
  CreateContactRecipientDTO,
  UpdateContactRecipientDTO,
  ContactRecipientResponseDTO,
} from '../../application/dtos/contact/contact-recipient.dto';
import {
  UpdateContactSettingsDTO,
  ContactSettingsResponseDTO,
  SendTestEmailDTO,
} from '../../application/dtos/contact/contact-settings.dto';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  stats?: ContactMessageStatsDTO;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export class HttpContactRepository implements IContactRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  // 1. Public Endpoints
  public async submitPublicMessage(data: CreateContactMessageDTO): Promise<{ id: string }> {
    const response = await this.httpClient.post<ApiResponse<{ id: string }>>(
      '/contact-messages',
      data
    );
    if (!response || !response.success) {
      throw new Error(response?.message || 'Error al enviar el mensaje de contacto.');
    }
    return response.data;
  }

  public async getPublicSettings(): Promise<{ isEnabled: boolean }> {
    const response = await this.httpClient.get<ApiResponse<{ isEnabled: boolean }>>(
      '/contact-settings/public'
    );
    if (!response || !response.success || !response.data) {
      return { isEnabled: true };
    }
    return response.data;
  }

  // 2. Admin Messages
  public async getMessages(
    filters?: ContactMessageFiltersDTO
  ): Promise<PaginatedContactMessagesResult> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.assignedToUserId) params.append('assignedToUserId', filters.assignedToUserId);
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await this.httpClient.get<ApiResponse<ContactMessageResponseDTO[]>>(
      `/contact-messages/admin${query}`
    );

    if (!response || !response.success || !Array.isArray(response.data)) {
      throw new Error('Formato de respuesta inválido al consultar mensajes de contacto.');
    }

    const messages = response.data.map((item) => this.mapMessageToDomain(item));
    const stats: ContactMessageStatsDTO = response.stats || {
      total: messages.length,
      new: 0,
      read: 0,
      inProgress: 0,
      responded: 0,
      archived: 0,
      spam: 0,
    };
    const pagination = response.pagination || {
      total: messages.length,
      page: filters?.page || 1,
      limit: filters?.limit || 20,
      totalPages: 1,
    };

    return {
      messages,
      stats,
      pagination,
    };
  }

  public async getMessageById(id: string): Promise<ContactMessageDetailResult> {
    const response = await this.httpClient.get<ApiResponse<ContactMessageDetailResponseDTO>>(
      `/contact-messages/${id}`
    );

    if (!response || !response.success || !response.data) {
      throw new Error('No se pudo obtener el detalle de la consulta de contacto.');
    }

    const message = this.mapMessageToDomain(response.data.message);
    const deliveries = (response.data.deliveries || []).map((d) =>
      this.mapDeliveryToDomain(d)
    );

    return {
      message,
      deliveries,
    };
  }

  public async updateMessageStatus(
    id: string,
    status: ContactMessageStatusType
  ): Promise<ContactMessage> {
    const response = await this.httpClient.patch<ApiResponse<ContactMessageResponseDTO>>(
      `/contact-messages/${id}/status`,
      { status }
    );

    if (!response || !response.success || !response.data) {
      throw new Error('Error al actualizar el estado del mensaje.');
    }

    return this.mapMessageToDomain(response.data);
  }

  public async assignMessage(
    id: string,
    assignedToUserId: string | null
  ): Promise<ContactMessage> {
    const response = await this.httpClient.patch<ApiResponse<ContactMessageResponseDTO>>(
      `/contact-messages/${id}/assign`,
      { assignedToUserId }
    );

    if (!response || !response.success || !response.data) {
      throw new Error('Error al asignar el mensaje al usuario.');
    }

    return this.mapMessageToDomain(response.data);
  }

  public async archiveMessage(id: string): Promise<ContactMessage> {
    const response = await this.httpClient.patch<ApiResponse<ContactMessageResponseDTO>>(
      `/contact-messages/${id}/archive`,
      { archived: true }
    );

    if (!response || !response.success || !response.data) {
      throw new Error('Error al archivar el mensaje.');
    }

    return this.mapMessageToDomain(response.data);
  }

  public async deleteMessage(id: string): Promise<void> {
    const response = await this.httpClient.delete<ApiResponse<null>>(`/contact-messages/${id}`);
    if (!response || !response.success) {
      throw new Error('Error al eliminar el mensaje de contacto.');
    }
  }

  public async retryEmail(messageId: string): Promise<ContactEmailDelivery[]> {
    const response = await this.httpClient.post<ApiResponse<ContactEmailDeliveryResponseDTO[]>>(
      `/contact-messages/${messageId}/retry-email`,
      {}
    );

    if (!response || !response.success || !Array.isArray(response.data)) {
      throw new Error('Error al reintentar el envío de correos.');
    }

    return response.data.map((d) => this.mapDeliveryToDomain(d));
  }

  // 3. Admin Recipients
  public async getRecipients(filters?: { activeOnly?: boolean }): Promise<ContactRecipient[]> {
    const query = filters?.activeOnly ? '?activeOnly=true' : '';
    const response = await this.httpClient.get<ApiResponse<ContactRecipientResponseDTO[]>>(
      `/contact-recipients${query}`
    );

    if (!response || !response.success || !Array.isArray(response.data)) {
      throw new Error('Error al consultar destinatarios de contacto.');
    }

    return response.data.map((r) => this.mapRecipientToDomain(r));
  }

  public async createRecipient(data: CreateContactRecipientDTO): Promise<ContactRecipient> {
    const response = await this.httpClient.post<ApiResponse<ContactRecipientResponseDTO>>(
      '/contact-recipients',
      data
    );

    if (!response || !response.success || !response.data) {
      throw new Error('Error al crear destinatario.');
    }

    return this.mapRecipientToDomain(response.data);
  }

  public async updateRecipient(
    id: string,
    data: UpdateContactRecipientDTO
  ): Promise<ContactRecipient> {
    const response = await this.httpClient.put<ApiResponse<ContactRecipientResponseDTO>>(
      `/contact-recipients/${id}`,
      data
    );

    if (!response || !response.success || !response.data) {
      throw new Error('Error al actualizar destinatario.');
    }

    return this.mapRecipientToDomain(response.data);
  }

  public async changeRecipientStatus(id: string, isActive: boolean): Promise<ContactRecipient> {
    const response = await this.httpClient.patch<ApiResponse<ContactRecipientResponseDTO>>(
      `/contact-recipients/${id}/status`,
      { isActive }
    );

    if (!response || !response.success || !response.data) {
      throw new Error('Error al modificar el estado del destinatario.');
    }

    return this.mapRecipientToDomain(response.data);
  }

  public async deleteRecipient(id: string): Promise<void> {
    const response = await this.httpClient.delete<ApiResponse<null>>(`/contact-recipients/${id}`);
    if (!response || !response.success) {
      throw new Error('Error al eliminar destinatario.');
    }
  }

  public async reorderRecipients(items: { id: string; displayOrder: number }[]): Promise<void> {
    const response = await this.httpClient.patch<ApiResponse<null>>(
      '/contact-recipients/reorder',
      { items }
    );

    if (!response || !response.success) {
      throw new Error('Error al reordenar destinatarios.');
    }
  }

  // 4. Admin Settings
  public async getSettings(): Promise<ContactSettings> {
    const response = await this.httpClient.get<ApiResponse<ContactSettingsResponseDTO>>(
      '/contact-settings'
    );

    if (!response || !response.success || !response.data) {
      throw new Error('Error al obtener la configuración de contacto.');
    }

    return this.mapSettingsToDomain(response.data);
  }

  public async updateSettings(data: UpdateContactSettingsDTO): Promise<ContactSettings> {
    const response = await this.httpClient.put<ApiResponse<ContactSettingsResponseDTO>>(
      '/contact-settings',
      data
    );

    if (!response || !response.success || !response.data) {
      throw new Error('Error al actualizar la configuración de contacto.');
    }

    return this.mapSettingsToDomain(response.data);
  }

  public async sendTestEmail(
    data: SendTestEmailDTO
  ): Promise<{ success: boolean; message?: string }> {
    const response = await this.httpClient.post<ApiResponse<null>>(
      '/contact-settings/test-email',
      data
    );

    return {
      success: response?.success ?? false,
      message: response?.message,
    };
  }

  // Mappers
  private mapMessageToDomain(dto: ContactMessageResponseDTO): ContactMessage {
    const raw = dto as any;
    return ContactMessage.create({
      id: raw.id ?? raw._id,
      fullName: raw.fullName ?? raw._fullName ?? '',
      email: raw.email ?? raw._email ?? '',
      phone: raw.phone ?? raw._phone ?? null,
      company: raw.company ?? raw._company ?? null,
      subject: raw.subject ?? raw._subject ?? '',
      message: raw.message ?? raw._message ?? '',
      status: raw.status ?? raw._status ?? 'NEW',
      assignedToUserId: raw.assignedToUserId ?? raw._assignedToUserId ?? null,
      assignedToUser: raw.assignedToUser ?? raw._assignedToUser,
      readAt: raw.readAt ?? raw._readAt,
      respondedAt: raw.respondedAt ?? raw._respondedAt,
      archivedAt: raw.archivedAt ?? raw._archivedAt,
      createdAt: raw.createdAt ?? raw._createdAt,
      updatedAt: raw.updatedAt ?? raw._updatedAt,
    });
  }

  private mapDeliveryToDomain(dto: ContactEmailDeliveryResponseDTO): ContactEmailDelivery {
    const raw = dto as any;
    return ContactEmailDelivery.create({
      id: raw.id ?? raw._id,
      contactMessageId: raw.contactMessageId ?? raw._contactMessageId,
      recipientEmail: raw.recipientEmail ?? raw._recipientEmail ?? '',
      subject: raw.subject ?? raw._subject ?? '',
      status: raw.status ?? raw._status ?? 'PENDING',
      providerMessageId: raw.providerMessageId ?? raw._providerMessageId,
      errorMessage: raw.errorMessage ?? raw._errorMessage,
      sentAt: raw.sentAt ?? raw._sentAt,
      createdAt: raw.createdAt ?? raw._createdAt,
    });
  }

  private mapRecipientToDomain(dto: ContactRecipientResponseDTO): ContactRecipient {
    const raw = dto as any;
    return ContactRecipient.create({
      id: raw.id ?? raw._id,
      name: raw.name ?? raw._name ?? '',
      email: raw.email ?? raw._email ?? '',
      isActive: raw.isActive !== undefined ? raw.isActive : (raw._isActive !== undefined ? raw._isActive : true),
      displayOrder: raw.displayOrder ?? raw._displayOrder ?? 0,
      createdAt: raw.createdAt ?? raw._createdAt,
      updatedAt: raw.updatedAt ?? raw._updatedAt,
    });
  }

  private mapSettingsToDomain(dto: ContactSettingsResponseDTO): ContactSettings {
    const raw = dto as any;
    return ContactSettings.create({
      id: raw.id ?? raw._id,
      isEnabled: raw.isEnabled !== undefined ? raw.isEnabled : (raw._isEnabled !== undefined ? raw._isEnabled : true),
      autoReplyEnabled:
        raw.autoReplyEnabled !== undefined
          ? raw.autoReplyEnabled
          : (raw._autoReplyEnabled !== undefined
          ? raw._autoReplyEnabled
          : true),
      autoReplySubject: raw.autoReplySubject ?? raw._autoReplySubject ?? '',
      autoReplyMessage: raw.autoReplyMessage ?? raw._autoReplyMessage ?? '',
      notificationSubjectPrefix:
        raw.notificationSubjectPrefix ?? raw._notificationSubjectPrefix ?? '',
      createdAt: raw.createdAt ?? raw._createdAt,
      updatedAt: raw.updatedAt ?? raw._updatedAt,
    });
  }
}
